import { Injectable } from '@angular/core';
import { orderBy, random } from 'lodash-es';
import momentRange from 'moment-range';
import moment from 'moment-timezone';

import { FieldKinds, Form, TableCellKinds, Template } from '@site-mate/dashpivot-shared-library';

// The require()-based block is for browsers, the import-based is for tests execution
const { extendMoment } = typeof require === 'function' ? require('moment-range') : momentRange;

const { range } = extendMoment(moment as any);

/**
 * This service is used to generate data for sample charts
 * For the sake of simplicity and to make it to work properly it contains hardcoded values
 */
@Injectable()
export class SampleChartDataService {
  readonly listItems = [
    'ABC Formwork',
    'Guideline Electricians',
    'Gyprock Express',
    'XYZ Contracting',
    'Sitemate',
    'Lifting Hroes On Site',
    'You got a lift?',
  ];

  readonly users = [
    { _id: 'modesta', fullName: 'Modesta Klein' },
    { _id: 'ayla', fullName: 'Ayla Schoen' },
    { _id: 'adeline', fullName: 'Adeline Weber' },
    { _id: 'myrna', fullName: 'Myrna Yost' },
    { _id: 'pablo', fullName: 'Pablo Sauer' },
    { _id: 'cooper', fullName: 'Cooper Runolfsson' },
  ];

  readonly dateFieldId = '5fa14d35c17e836c106abae1';
  readonly templateId = '5fa14d35c17e836c106abae2';
  readonly itemId = '5fa14d35c17e836c106abae3';
  readonly columnId = '5fa14d35c17e836c106abae4';
  readonly listId = '5fa14d35c17e836c106abae5';

  private forms = [];

  constructor() {
    const dateRange = range(moment().subtract(1, 'year'), moment().endOf('month'));
    this.forms = Array.from(dateRange.by('day')).map(() =>
      this.generateForm(dateRange, this.listItems, this.users),
    );
    this.forms = orderBy(this.forms, 'createdAt', 'desc');
  }

  getSampleForms(params): Form[] {
    const sortField = params.sortField ? JSON.parse(params.sortField) : null;
    const dateRange = sortField ? range(new Date(sortField.startDate), new Date(sortField.endDate)) : range();

    return this.forms.filter((form) => {
      const dateFilter = dateRange.contains(form.createdAt);
      let listFilter = true;
      let createdByFilter = true;

      if (params[this.itemId]) {
        const filter = JSON.parse(params[this.itemId]);
        listFilter = filter[this.listId].includes(form.items[0].rows[0].columns[1].value);
      }

      if (params.createdBy) {
        const filter = JSON.parse(params.createdBy);
        createdByFilter = filter.includes(form.createdBy._id);
      }

      return dateFilter && listFilter && createdByFilter;
    });
  }

  private generateForm(dateRange, listItems, users): Form {
    return {
      createdAt: new Date(random(+dateRange.start, +dateRange.end)),
      createdBy: users[random(users.length - 1)],
      items: [
        {
          _id: this.itemId,
          kind: FieldKinds.Table,
          columns: [
            {
              _id: this.columnId,
              kind: TableCellKinds.Number,
            },
            {
              _id: this.listId,
              kind: TableCellKinds.List,
            },
          ],
          rows: [
            {
              columns: [
                {
                  headerColumnId: this.columnId,
                  value: random(20),
                },
                {
                  headerColumnId: this.listId,
                  value: listItems[random(listItems.length - 1)],
                },
              ],
            },
          ],
        },
      ],
    } as Form;
  }

  getSampleCount() {
    return {
      totalQuantity: this.forms.reduce((acc, form) => {
        return acc + form.items[0].rows[0].columns[0].value;
      }, 0),
    };
  }

  getSampleTemplate(): Template {
    const template = {
      name: 'Site Diary',
      _id: this.templateId,
      parents: [],
      items: [
        {
          _id: this.dateFieldId,
          description: 'Shift Time',
          kind: 'date',
          isSingleDate: true,
        },
        {
          _id: this.itemId,
          kind: 'table',
          description: 'Plant',
          displayRowsMode: 'horizontal',
          rows: [],
          columns: [
            {
              _id: this.columnId,
              heading: 'Hours',
              kind: 'number',
            },
            {
              _id: this.listId,
              heading: 'Subcontractor',
              kind: 'list',
              listSource: 'manual',
              manualListItems: this.listItems,
            },
          ],
        },
      ],
      instanceName: 'Site Diary',
      uniqueAppId: 'DP-COM-0001',
      columns: [],
      staticAttachments: [],
      sortFormsBy: [this.dateFieldId],
      displayFormThumbnailBy: [],
    };

    return template as Template;
  }
}
